import { useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { formatToCompactNumber } from "app/utils/utils";

export interface GapAnalysisRow {
  year: number;
  planYear: number;
  insuredAge?: number;
  isActual?: boolean;
  current?: number;
  required?: number;
  /** Signed dollar value (sign matches percent); shown when the $ view is active. */
  amount?: number | null;
  percent: number;
  isSurplus?: boolean;
}

type GapViewMode = "percent" | "dollar";

interface GapAnalysisChartProps {
  data?: GapAnalysisRow[];
  showProjections?: boolean;
  isDark?: boolean;
  /** X-axis label dimension, driven by the Performance Chart date toggle. */
  xAxisDimension?: string;
}

const SURPLUS_COLOR = "#22C55E";
const DEFICIT_COLOR = "#EF4444";

// Accent colors for hexagon markers.
const SURPLUS_ACCENT = "#1DA750";
const DEFICIT_ACCENT = "#FF3D32";

// Bar border colors.
const SURPLUS_BORDER = "#4AB973";
const DEFICIT_BORDER = "#FF645B";

const CHART_HEIGHT = 600;
const GRID_TOP = 38;
const GRID_BOTTOM = 60; // leaves room for the dataZoom scroll slider
// Symmetric value axis → zero baseline sits at the vertical center of the plot area.
const ZERO_BASELINE_PCT =
  ((GRID_TOP + (CHART_HEIGHT - GRID_TOP - GRID_BOTTOM) / 2) / CHART_HEIGHT) * 100;
// How many bars to show in the initial window (user can zoom/scroll to see the rest).
const INITIAL_VISIBLE_BARS = 8;

const makeHexSymbol = (color: string): string => {
  // Solid-filled hexagon (no clip / no transparency) so the baseline behind it is fully covered.
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 20 18" fill="none">` +
    `<path d="M13.4687 1.26519H13.4729L13.5235 1.26625C14.1466 1.27152 14.7311 1.61259 15.0481 2.15663` +
    `L18.5009 8.08194C18.831 8.64812 18.831 9.35188 18.5009 9.91858L15.0486 15.8439C14.728 16.3937` +
    ` 14.1372 16.7353 13.5068 16.7353H6.49373C5.86282 16.7353 5.27205 16.3937 4.9519 15.8444L3.49975` +
    ` 13.3525L1.49907 9.91911C1.16902 9.35294 1.16902 8.64917 1.49907 8.08247L4.9519 2.15663C5.27205` +
    ` 1.60679 5.86334 1.26519 6.49373 1.26519H13.4687Z" fill="${color}"/>` +
    `</svg>`;
  return `image://data:image/svg+xml,${encodeURIComponent(svg)}`;
};

function GapAnalysisChart({
  data,
  showProjections = false,
  isDark = false,
  xAxisDimension = "year",
}: GapAnalysisChartProps) {
  const rows = data ?? [];
  // Independent of the Performance Chart toggle: % is the default view, $ on toggle.
  const [viewMode, setViewMode] = useState<GapViewMode>("percent");
  const isDollar = viewMode === "dollar";

  // echarts-for-react only auto-resizes on window resize, not container resize. Without this
  // the chart can render blank until an unrelated re-render (e.g. toggling TrueIQ Protect)
  // forces a redraw. A ResizeObserver keeps it in sync with its container.
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => chartRef.current?.getEchartsInstance?.()?.resize());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const option = useMemo(() => {
    const textColor = isDark ? "#e2e8f0" : "#334155";
    const surplusColor = isDark ? "#86efac" : SURPLUS_COLOR;
    const deficitColor = isDark ? "#fca5a5" : DEFICIT_COLOR;
    const axisBorderColor = isDark ? "#3A3A50" : "#d1d5db";
    const projColor = isDark ? "rgba(255,255,255,0.08)" : "#EBEBEB";
    const projBorderColor = isDark ? "#6B7280" : "#9CA3AF";

    type CombinedRow = GapAnalysisRow & { isProjection: boolean };

    // Projected rows (isActual === false) only appear when the Performance Chart "Projected"
    // toggle is on, and are then styled dashed/gray. When off, they're hidden entirely.
    const allRows: CombinedRow[] = rows
      .filter((r) => showProjections || r.isActual !== false)
      .map((r) => ({ ...r, isProjection: showProjections && r.isActual === false }));

    // Pad the category axis to a minimum slot count so few bars stay left-aligned (fixed width)
    // instead of being spread/centered across the full width. Bar/scatter data (length =
    // allRows) maps to the first slots; the trailing padded slots stay empty.
    const slotCount = Math.max(allRows.length, INITIAL_VISIBLE_BARS);
    const categories = Array.from({ length: slotCount }, (_, i) =>
      i < allRows.length ? String(allRows[i].year) : `__pad_${i}`,
    );

    // Baseline label follows the Performance Chart date toggle (Year / Plan Year / Insured Age).
    const axisLabel = (r: GapAnalysisRow) => {
      if (xAxisDimension === "planYear") return String(r.planYear);
      if (xAxisDimension === "insuredAge") return r.insuredAge == null ? "" : `Age ${r.insuredAge}`;
      return String(r.year);
    };
    // isSurplus → bar goes up (green); otherwise down (red).
    const isRowSurplus = (r: GapAnalysisRow) => r.isSurplus ?? r.percent >= 0;
    // Bar magnitude: percent capped at 100 (>100% coverage reads as 100%), or raw dollar amount.
    const magnitude = (r: GapAnalysisRow) =>
      isDollar ? Math.abs(r.amount ?? 0) : Math.min(100, Math.abs(r.percent ?? 0));
    const rowValue = (r: GapAnalysisRow) => (isRowSurplus(r) ? 1 : -1) * magnitude(r);
    const fmtValue = (v: number) =>
      isDollar ? formatToCompactNumber(v, { prefix: "$" }) : `${Math.round(Math.abs(v))}%`;
    // Full-precision currency (with thousands separators) for the hover breakdown.
    const fmtFullCurrency = (v: number) => `$${Math.round(Math.abs(v)).toLocaleString("en-US")}`;

    // Hover tooltip: shows the gap math, e.g. ($110,000 / $100,000) − 1 = +10%.
    const tooltipFormatter = (params: any) => {
      const p = Array.isArray(params) ? params[0] : params;
      const row = allRows[p?.dataIndex];
      if (!row) return "";
      const surplus = isRowSurplus(row);
      const accent = surplus ? surplusColor : deficitColor;
      const title = axisLabel(row) || String(row.year);
      // Real (uncapped) percent — the tooltip shows the true value even when the bar caps at 100%.
      const pct = row.percent ?? 0;
      const signedPct = `${pct >= 0 ? "+" : "−"}${Math.round(Math.abs(pct))}%`;
      const hasMath =
        typeof row.current === "number" && typeof row.required === "number" && row.required !== 0;
      const lines: string[] = [];
      if (hasMath) {
        const cur = fmtFullCurrency(row.current as number);
        const req = fmtFullCurrency(row.required as number);
        lines.push(
          `<div>Current: <b>${cur}</b></div>`,
          `<div>Required: <b>${req}</b></div>`,
          `<div style="margin-top:4px;">(${cur} / ${req}) − 1 = <b style="color:${accent}">${signedPct}</b></div>`,
        );
      } else {
        lines.push(`<div>Gap: <b style="color:${accent}">${signedPct}</b></div>`);
      }
      return `<div style="font-family:system-ui,sans-serif;font-size:12px;line-height:1.5;"><div style="font-weight:700;margin-bottom:2px;">${title}</div>${lines.join(
        "",
      )}</div>`;
    };

    let roundedMax: number;
    let axisInterval: number;
    if (isDollar) {
      const maxAbs = Math.max(...allRows.map((r) => Math.abs(r.amount ?? 0)), 100_000);
      roundedMax = Math.ceil(maxAbs / 100_000) * 100_000;
      axisInterval = roundedMax / 4;
    } else {
      roundedMax = 100;
      axisInterval = 20;
    }

    return {
      animation: false,
      backgroundColor: isDark ? "#2A2A3D" : "#ffffff",
      tooltip: {
        show: true,
        trigger: "item" as const,
        confine: true,
        backgroundColor: isDark ? "#1e2030" : "#ffffff",
        borderColor: isDark ? "#3A3A50" : "#e5e7eb",
        borderWidth: 1,
        padding: 10,
        textStyle: { color: textColor, fontSize: 12 },
        extraCssText: "box-shadow: 0 4px 12px rgba(0,0,0,0.15); border-radius: 8px;",
        formatter: tooltipFormatter,
      },
      legend: { show: false },
      grid: { left: isDollar ? 64 : 52, right: 24, top: GRID_TOP, bottom: GRID_BOTTOM },
      // Native ECharts scroll slider (same as the Performance Chart) — user can widen/narrow
      // the window to view any number of bars.
      dataZoom: [
        {
          type: "slider" as const,
          show: allRows.length > INITIAL_VISIBLE_BARS,
          startValue: 0,
          endValue: Math.min(slotCount, INITIAL_VISIBLE_BARS) - 1,
          minValueSpan: 1,
          maxValueSpan: slotCount,
          height: 18,
          bottom: 14,
          borderColor: "transparent",
          fillerColor: isDark ? "rgba(167,183,204,0.3)" : "rgba(167,183,204,0.4)",
          handleStyle: {
            color: isDark ? "#ffffff" : "#333333",
            borderColor: isDark ? "#4A4A60" : "#E5E7EB",
          },
          textStyle: { color: textColor, fontSize: 10 },
          showDetail: false,
          showDataShadow: true,
          realtime: true,
          filterMode: "filter" as const,
          xAxisIndex: 0,
        },
      ],
      xAxis: {
        type: "category" as const,
        data: categories,
        axisLine: { lineStyle: { color: axisBorderColor, width: 1 } },
        axisTick: { show: false },
        axisLabel: { show: false },
      },
      yAxis: {
        type: "value" as const,
        min: -roundedMax,
        max: roundedMax,
        interval: axisInterval,
        axisLabel: {
          color: textColor,
          fontSize: 11,
          formatter: (v: number) => (v === 0 ? "" : fmtValue(Math.abs(v))),
        },
        splitLine: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          type: "bar" as const,
          barMaxWidth: 90,
          label: { show: true },
          // Hover triggers the gap-math tooltip; emphasis stays off so there's no highlight.
          silent: false,
          emphasis: { disabled: true },
          data: allRows.map((row) => ({
            value: rowValue(row),
            itemStyle: row.isProjection
              ? {
                  color: projColor,
                  borderColor: projBorderColor,
                  borderType: [4, 4] as number[],
                  borderWidth: 2,
                  borderRadius: 4,
                }
              : {
                  color: isRowSurplus(row)
                    ? isDark
                      ? "rgba(34,197,94,0.40)"
                      : "rgba(134,239,172,0.70)"
                    : isDark
                    ? "rgba(239,68,68,0.35)"
                    : "rgba(252,165,165,0.70)",
                  borderColor: isRowSurplus(row) ? SURPLUS_BORDER : DEFICIT_BORDER,
                  borderWidth: 2,
                  borderRadius: 4,
                },
            label: row.isProjection
              ? {
                  show: true,
                  position: isRowSurplus(row) ? ("bottom" as const) : ("top" as const),
                  distance: 5,
                  formatter: axisLabel(row),
                  color: isDark ? "#4B5563" : "#9CA3AF",
                  fontSize: 11,
                  fontFamily: "system-ui, sans-serif",
                }
              : {
                  show: true,
                  position: isRowSurplus(row) ? ("top" as const) : ("bottom" as const),
                  distance: 8,
                  formatter: fmtValue(Math.abs(rowValue(row))),
                  color: isRowSurplus(row) ? surplusColor : deficitColor,
                  fontWeight: 700,
                  fontSize: 13,
                  fontFamily: "system-ui, sans-serif",
                },
          })),
        },
        {
          type: "scatter" as const,
          z: 20,
          silent: true,
          symbolSize: [32, 32],
          symbolKeepAspect: true,
          label: { show: true },
          data: allRows.map((row, i) => ({
            value: [i, 0],
            symbol: row.isProjection
              ? "none"
              : makeHexSymbol(isRowSurplus(row) ? SURPLUS_ACCENT : DEFICIT_ACCENT),
            label: row.isProjection
              ? { show: false }
              : {
                  show: true,
                  formatter: axisLabel(row),
                  position: isRowSurplus(row) ? ("bottom" as const) : ("top" as const),
                  distance: 5,
                  color: textColor,
                  fontSize: 11,
                  fontFamily: "system-ui, sans-serif",
                },
          })),
        },
      ],
    };
  }, [rows, showProjections, isDark, isDollar, xAxisDimension]);

  return (
    <div className="mt-2">
      <p className="text-xl conthrax-font mb-1 px-1 text-[#1e2d4d] dark:text-gray-100">
        Gap Analysis
      </p>
      <div className="relative" ref={containerRef}>
        <ReactECharts
          ref={chartRef}
          option={option}
          style={{ height: CHART_HEIGHT }}
          notMerge
          lazyUpdate
        />
        {/* Anchor the $/% toggle on the (off-center, due to the slider) zero baseline. */}
        <button
          type="button"
          onClick={() => setViewMode((m) => (m === "percent" ? "dollar" : "percent"))}
          title={isDollar ? "Show percentages" : "Show dollar values"}
          style={{
            top: `${ZERO_BASELINE_PCT}%`,
            transform: "translateY(-50%)",
            width: 36,
            height: 36,
            minWidth: 36,
            padding: 9.6,
            borderRadius: 7.2,
            borderWidth: 1.2,
            borderStyle: "solid",
            borderColor: isDark ? "#4A90E2" : "#85B2F9",
            lineHeight: 1,
          }}
          className={`absolute ml-2.5 conthrax-font left-1 z-10 flex items-center justify-center text-base font-bold shadow-sm transition-colors ${
            isDark
              ? "bg-[#1e2030] text-[#93C5FD] hover:bg-[#262842]"
              : "bg-white text-[#004781] hover:bg-[#EAF2FF]"
          }`}
        >
          {isDollar ? "%" : "$"}
        </button>
      </div>
    </div>
  );
}

export default GapAnalysisChart;
